import { useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import SuperAdminLayout from '../../components/feature/SuperAdminLayout';

interface Company {
  id: string;
  name: string;
  email: string;
  manager: string;
  phone: string;
  plan: string;
  status: 'pending' | 'active' | 'inactive';
  registeredDate: string;
  students: number;
  certificates: number;
}

interface Activity {
  id: string;
  type: 'company_registered' | 'plan_upgraded' | 'certificate_issued' | 'payment_completed';
  company: string;
  description: string;
  timestamp: string;
}

const SuperAdminDashboard = () => {
  const [pendingCompanies] = useState<Company[]>([
    {
      id: 'C001',
      name: '테크 아카데미',
      email: 'admin@techacademy.com',
      manager: '김철수',
      phone: '010-1234-5678',
      plan: 'Professional',
      status: 'pending',
      registeredDate: '2024-01-15',
      students: 0,
      certificates: 0,
    },
    {
      id: 'C002',
      name: '글로벌 교육센터',
      email: 'contact@globaledu.com',
      manager: '이영희',
      phone: '010-2345-6789',
      plan: 'Enterprise',
      status: 'pending',
      registeredDate: '2024-01-14',
      students: 0,
      certificates: 0,
    },
    {
      id: 'C003',
      name: '스마트 러닝',
      email: 'info@smartlearning.com',
      manager: '박민수',
      phone: '010-3456-7890',
      plan: 'Basic',
      status: 'pending',
      registeredDate: '2024-01-13',
      students: 0,
      certificates: 0,
    },
  ]);

  const [recentActivities] = useState<Activity[]>([
    {
      id: 'A001',
      type: 'company_registered',
      company: '테크 아카데미',
      description: '새로운 고객사가 등록되었습니다',
      timestamp: '2024-01-15 14:30',
    },
    {
      id: 'A002',
      type: 'plan_upgraded',
      company: '디지털 교육원',
      description: 'Basic에서 Professional로 플랜 업그레이드',
      timestamp: '2024-01-15 13:20',
    },
    {
      id: 'A003',
      type: 'certificate_issued',
      company: 'IT 교육센터',
      description: '50개의 수료증이 발급되었습니다',
      timestamp: '2024-01-15 11:45',
    },
    {
      id: 'A004',
      type: 'payment_completed',
      company: '온라인 아카데미',
      description: '월간 구독료 결제 완료 (₩299,000)',
      timestamp: '2024-01-15 10:15',
    },
  ]);

  const [stats] = useState({
    totalCompanies: 48,
    activeCompanies: 42,
    pendingApproval: 3,
    totalRevenue: 14250000,
    monthlyRevenue: 8500000,
    totalCertificates: 2847,
  });

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const handleApprove = (companyId: string) => {
    console.log('Approving company:', companyId);
    alert(`${companyId} 고객사가 승인되었습니다.`);
  };

  const handleReject = (companyId: string) => {
    console.log('Rejecting company:', companyId);
    if (confirm('정말 이 고객사를 거부하시겠습니까?')) {
      alert(`${companyId} 고객사가 거부되었습니다.`);
    }
  };

  const handleViewDetail = (company: Company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'company_registered':
        return 'ri-building-line';
      case 'plan_upgraded':
        return 'ri-arrow-up-circle-line';
      case 'certificate_issued':
        return 'ri-award-line';
      case 'payment_completed':
        return 'ri-money-dollar-circle-line';
      default:
        return 'ri-information-line';
    }
  };

  const getActivityColor = (type: Activity['type']) => {
    switch (type) {
      case 'company_registered':
        return 'bg-blue-100 text-blue-600';
      case 'plan_upgraded':
        return 'bg-green-100 text-green-600';
      case 'certificate_issued':
        return 'bg-purple-100 text-purple-600';
      case 'payment_completed':
        return 'bg-yellow-100 text-yellow-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <SuperAdminLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">슈퍼 관리자 대시보드</h1>
                <p className="mt-1 text-sm text-gray-600">전체 시스템 현황을 한눈에 확인하세요</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Link to="/super-admin/companies" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <i className="ri-building-line text-2xl text-blue-600"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600">총 고객사</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCompanies}</p>
              </div>
            </div>
          </Link>

          <Link to="/super-admin/revenue" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600">이번 달 수익</p>
                <p className="text-2xl font-bold text-gray-900">₩{stats.monthlyRevenue.toLocaleString()}</p>
              </div>
            </div>
          </Link>

          <Link to="/super-admin/commission" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                <i className="ri-percent-line text-2xl text-purple-600"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600">수수료 설정</p>
                <p className="text-2xl font-bold text-gray-900">15%</p>
              </div>
            </div>
          </Link>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                <i className="ri-file-list-3-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600">총 발급 수료증</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCertificates.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Pending Approvals */}
          <div>
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">승인 대기 업체</h2>
                <Link to="/super-admin/companies" className="text-sm text-blue-600 hover:text-blue-700">
                  전체 보기 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="space-y-4">
                {pendingCompanies.map((company) => (
                  <div key={company.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{company.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{company.manager}</p>
                      </div>
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                        {company.plan}
                      </span>
                    </div>

                    <div className="text-sm text-gray-600 mb-4">
                      <p>등록일: {company.registeredDate}</p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleApprove(company.id)}
                      >
                        <i className="ri-check-line"></i>
                        승인
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleReject(company.id)}
                      >
                        <i className="ri-close-line"></i>
                        거부
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetail(company)}
                      >
                        <i className="ri-eye-line"></i>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Recent Activities */}
          <div>
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">최근 시스템 활동</h2>
              </div>

              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.type)}`}>
                      <i className={`${getActivityIcon(activity.type)} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{activity.company}</p>
                      <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">고객사 상세 정보</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">기본 정보</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">고객사 ID</p>
                    <p className="font-medium text-gray-900">{selectedCompany.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">고객사명</p>
                    <p className="font-medium text-gray-900">{selectedCompany.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">이메일</p>
                    <p className="font-medium text-gray-900">{selectedCompany.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">담당자명</p>
                    <p className="font-medium text-gray-900">{selectedCompany.manager}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">전화번호</p>
                    <p className="font-medium text-gray-900">{selectedCompany.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">등록일</p>
                    <p className="font-medium text-gray-900">{selectedCompany.registeredDate}</p>
                  </div>
                </div>
              </div>

              {/* Plan Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">구독 정보</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">플랜</p>
                    <p className="font-medium text-gray-900">{selectedCompany.plan}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">상태</p>
                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                      selectedCompany.status === 'active' ? 'bg-green-100 text-green-700' :
                      selectedCompany.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {selectedCompany.status === 'active' ? '활성' :
                       selectedCompany.status === 'pending' ? '대기중' : '비활성'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Usage Stats */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">사용 현황</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">등록 학습자</p>
                    <p className="font-medium text-gray-900">{selectedCompany.students}명</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">발급 수료증</p>
                    <p className="font-medium text-gray-900">{selectedCompany.certificates}개</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="primary"
                  className="flex-1"
                  onClick={() => {
                    handleApprove(selectedCompany.id);
                    setShowDetailModal(false);
                  }}
                >
                  <i className="ri-check-line"></i>
                  승인
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    handleReject(selectedCompany.id);
                    setShowDetailModal(false);
                  }}
                >
                  <i className="ri-close-line"></i>
                  거부
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </SuperAdminLayout>
  );
};

export default SuperAdminDashboard;
